import _ from "lodash"
import { createSlice } from "@reduxjs/toolkit"
import { TomorrowSchemaState } from "./ScheduleSchema"
import { validateStateInDev } from "./validateStateInDev"
import {
	deallocatePatient,
	transferPatientsBetweenNursesAndChairs,
	markNurseAsEdited,
	resetAllEdits
} from "./atomicActions"

const tomorrowSlice = createSlice({
	name: "tomorrow",
	initialState: {
		isLocked: false,
		triedToLock: false,
		acuityMix: {},
		chairSchedule: [],
		nurseSchedule: [],
		// See scheduleSlice for baseline semantics — used by useSchedulePatch
		// to diff and send only changed nurses/chairs.
		nurseScheduleBaseline: [],
		chairScheduleBaseline: [],
		utilizationCurve: [],
		chargeNurse: null,
		patientSchedule: [],
		editedPatients: [],
		editedNurses: [],
		isEditing: false,
		isTomorrowMode: false,
		openTime: "08:00",
		closeTime: "20:00",
		sftpReassignment: [],
		status: "idle", // Track loading states: 'idle', 'loading', 'succeeded', 'failed'
		error: null, // Store error messages if any
		documentId: null // Add _id to the state
	},
	reducers: {
		//ATOMIC ACTIONS
		deallocatePatientTomorrow: deallocatePatient(TomorrowSchemaState),

		transferPatientsBetweenNursesAndChairsTomorrow:
			transferPatientsBetweenNursesAndChairs(TomorrowSchemaState),

		markNurseAsEditedTomorrow: markNurseAsEdited(TomorrowSchemaState),

		resetAllEditsTomorrow: resetAllEdits(TomorrowSchemaState),

		//NON-ATOMIC ACTIONS
		setTomorrowLoading: state => {
			state.status = "loading"
			state.error = null // Clear any previous error
		},
		setTomorrowMode: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.isTomorrowMode = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowData: (state, action) => {
			const newState = _.cloneDeep(state)

			newState.status = "succeeded"
			const payload = action.payload
			newState.isLocked = payload.locked
			newState.acuityMix = payload.acuityMix
			newState.chairSchedule = payload.chairSchedule
			newState.nurseSchedule = payload.nurseSchedule
			newState.nurseScheduleBaseline = payload.nurseSchedule || newState.nurseScheduleBaseline
			newState.chairScheduleBaseline = payload.chairSchedule || newState.chairScheduleBaseline
			newState.utilizationCurve = payload.utilizationCurve
			newState.patientSchedule = payload.patientSchedule
			
			newState.chargeNurse = payload.chargeNurse
			newState.openTime = payload.openTime
			newState.closeTime = payload.closeTime
			newState.documentId = payload._id
			newState.error = null // Clear any previous error on success

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowError: (state, action) => {
			state.status = "failed"
			state.error = action.payload // Store the error message
		},
		// See scheduleSlice.setBaselineToCurrent for semantics.
		setTomorrowBaselineToCurrent: state => {
			state.nurseScheduleBaseline = _.cloneDeep(state.nurseSchedule)
			state.chairScheduleBaseline = _.cloneDeep(state.chairSchedule)
		},
		updateTomorrowSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientSchedule, nurseSchedule, chairSchedule, chargeNurse } = action.payload

			const newState = _.cloneDeep(state)

			if (patientSchedule) {
				newState.patientSchedule = patientSchedule
			}
			if (nurseSchedule) {
				newState.nurseSchedule = nurseSchedule
			}
			if (chairSchedule) {
				newState.chairSchedule = chairSchedule
			}
			if (chargeNurse) {
				newState.chargeNurse = chargeNurse
			}

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowLockStatus: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.isLocked = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowTriedToLock: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.triedToLock = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowAcuityMix: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.acuityMix = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowChairSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.chairSchedule = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowNurseSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.nurseSchedule = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowUtilizationCurve: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.utilizationCurve = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		removeTomorrowNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const nurseId = action.payload
			const editedNurseIndex = state.editedNurses.findIndex(
				nurse => nurse.nurseId === nurseId
			)

			const newState = _.cloneDeep(state)
			newState.editedNurses[editedNurseIndex].deleted = true
			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		addTomorrowNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newNurse = action.payload

			const newState = _.cloneDeep(state)

			newState.editedNurses.push({
				nurseId: newNurse.nurseId,
				added: true,
				deleted: false,
				name: false,
				scheduledStartTime: false,
				scheduledEndTime: false,
				scheduledLunchStartTime: false,
				scheduledLunchEndTime: false
			})

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		updateTomorrowEditedNurses: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { nurseId, fieldName } = action.payload

			const newState = _.cloneDeep(state)

			newState.editedNurses = newState.editedNurses.map(nurse => {
				if (nurse.nurseId === nurseId) {
					return {
						...nurse,
						[fieldName]: true // Set the specific field to true
					}
				}
				return nurse
			})

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		updateTomorrowNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const updatedNurse = action.payload

			const newState = _.cloneDeep(state)

			newState.nurseSchedule = newState.nurseSchedule.map(nurse =>
				nurse.nurseId === updatedNurse.nurseId ? updatedNurse : nurse
			)

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		updateTomorrowPatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientId, fieldName } = action.payload

			const newState = _.cloneDeep(state)

			newState.editedPatients = newState.editedPatients.map(patient => {
				if (patient.patientId === patientId) {
					return {
						...patient,
						[fieldName]: true
					}
				}
				return patient
			})

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		addTomorrowPatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientId, fieldName } = action.payload

			const newState = _.cloneDeep(state)

			// Check if patient already exists in editedPatients
			const existingPatient = newState.editedPatients.find(p => p.patientId === patientId)

			if (!existingPatient) {
				// Add new patient if it doesn't exist
				newState.editedPatients.push({
					patientId,
					assignedNurse: fieldName === "assignedNurse",
					assignedChair: fieldName === "assignedChair",
					acuity: fieldName === "acuity",
					cancellationConsideredForReassignment:
						fieldName === "cancellationConsideredForReassignment",
					pendingChanges: fieldName === "pendingChanges",
					notes: fieldName === "notes",
					shownCancelledPatient: fieldName === "shownCancelledPatient"
				})
			} else {
				// Update existing patient
				newState.editedPatients = newState.editedPatients.map(patient => {
					if (patient.patientId === patientId) {
						return {
							...patient,
							assignedNurse: fieldName === "assignedNurse",
							assignedChair: fieldName === "assignedChair",
							acuity: fieldName === "acuity",
							cancellationConsideredForReassignment:
								fieldName === "cancellationConsideredForReassignment",
							pendingChanges: fieldName === "pendingChanges",
							notes: fieldName === "notes",
							shownCancelledPatient: fieldName === "shownCancelledPatient"
						}
					}
					return patient
				})
			}

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		removeTomorrowPatientField: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientId } = action.payload

			const newState = _.cloneDeep(state)

			// Remove the patient entirely from editedPatients
			newState.editedPatients = newState.editedPatients.filter(
				patient => patient.patientId !== patientId
			)

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		resetTomorrowPatientEdits: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientId } = action.payload

			const newState = _.cloneDeep(state)

			newState.editedPatients = newState.editedPatients.map(patient => {
				if (patient.patientId === patientId) {
					return {
						...patient,
						acuity: false,
						assignedNurse: false,
						assignedChair: false,
						pendingChanges: false,
						notes: false,
						cancellationConsideredForReassignment: false,
						shownCancelledPatient: false
					}
				}
				return patient
			})

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		updateTomorrowPatientInSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { updatedPatient } = action.payload

			const newState = _.cloneDeep(state)

			newState.patientSchedule = newState.patientSchedule.map(patient =>
				patient.patientId === updatedPatient.patientId ? updatedPatient : patient
			)

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		addTomorrowPatientToEditedPatients: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newPatient = action.payload

			const newState = _.cloneDeep(state)

			// Ensure patient has the same structure as adjustPatientSchedule output
			const patientEntry = {
				patientId: newPatient.patientId,
				acuity: false,
				assignedNurse: false,
				assignedChair: false,
				pendingChanges: false,
				notes: false,
				cancellationConsideredForReassignment: false,
				shownCancelledPatient: false
			}
			newState.editedPatients.push(patientEntry)

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		removeTomorrowPatientFromEditedPatients: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const patientId = action.payload

			const newState = _.cloneDeep(state)

			newState.editedPatients = newState.editedPatients.filter(p => p.patientId !== patientId)

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowSftpReassignment: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.sftpReassignment = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		},
		setTomorrowIsEditing: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			newState.isEditing = action.payload

			const validatedNewState = validateStateInDev(TomorrowSchemaState, newState)
			return validatedNewState
		}
	}
})

export const {
	deallocatePatientTomorrow,
	transferPatientsBetweenNursesAndChairsTomorrow,
	markNurseAsEditedTomorrow,
	resetAllEditsTomorrow,
	setTomorrowLoading,
	setTomorrowMode,
	setTomorrowData,
	setTomorrowError,
	updateTomorrowSchedule,
	setTomorrowLockStatus,
	setTomorrowAcuityMix,
	setTomorrowChairSchedule,
	setTomorrowNurseSchedule,
	setTomorrowUtilizationCurve,
	addTomorrowNurse,
	updateTomorrowEditedNurses,
	removeTomorrowNurse,
	updateTomorrowNurse,
	updateTomorrowPatientField,
	addTomorrowPatientField,
	removeTomorrowPatientField,
	resetTomorrowPatientEdits,
	updateTomorrowPatientInSchedule,
	addTomorrowPatientToEditedPatients,
	removeTomorrowPatientFromEditedPatients,
	setTomorrowSftpReassignment,
	setTomorrowIsEditing,
	setTomorrowBaselineToCurrent
} = tomorrowSlice.actions

export default tomorrowSlice.reducer
